import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Pressable,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  listShippingProvinces,
  listShippingCities,
  listShippingPostalCodes,
} from '../services/shippingAddressApi';

export default function AddressWilayahPicker({ visible, onClose, onSelect }) {
  const [tab, setTab] = useState('provinsi');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [postal, setPostal] = useState([]);
  const [prov, setProv] = useState(null);
  const [kota, setKota] = useState(null);

  useEffect(() => {
    if (!visible) return;
    setTab('provinsi');
    setQuery('');
    setCities([]);
    setPostal([]);
    setProv(null);
    setKota(null);
    let cancelled = false;
    setLoading(true);
    listShippingProvinces()
      .then((rows) => {
        if (!cancelled) setProvinces(rows);
      })
      .catch(() => {
        if (!cancelled) setProvinces([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible]);

  const items = tab === 'provinsi' ? provinces : tab === 'kota' ? cities : postal;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const hay = `${item.nama || ''} ${item.label || ''} ${item.nama_kecamatan || ''} ${item.kodepos || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, query]);

  const chooseProvinsi = async (row) => {
    setProv(row);
    setKota(null);
    setPostal([]);
    setQuery('');
    setTab('kota');
    setLoading(true);
    try {
      setCities(await listShippingCities(row.kode));
    } catch {
      setCities([]);
    } finally {
      setLoading(false);
    }
  };

  const chooseKota = async (row) => {
    setKota(row);
    setQuery('');
    setTab('kodepos');
    setLoading(true);
    try {
      setPostal(await listShippingPostalCodes(row.kode));
    } catch {
      setPostal([]);
    } finally {
      setLoading(false);
    }
  };

  const choosePostal = (row) => {
    onSelect({
      nama_provinsi: prov?.nama || '',
      nama_kota: kota?.nama || '',
      nama_kecamatan: row.nama_kecamatan || '',
      kodepos: row.kodepos || '',
      kode: row.kode || '',
    });
    onClose();
  };

  const tabDisabled = {
    provinsi: false,
    kota: !prov,
    kodepos: !kota,
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <Pressable style={styles.backdrop} onPress={onClose} />
        <View style={styles.sheet}>
          <View style={styles.head}>
            <Text style={styles.title}>Pilih wilayah</Text>
            <Pressable onPress={onClose} hitSlop={12}>
              <Ionicons name="close" size={22} color="#64748B" />
            </Pressable>
          </View>
          <View style={styles.tabs}>
            {[
              { id: 'provinsi', label: 'Provinsi' },
              { id: 'kota', label: 'Kota/Kab' },
              { id: 'kodepos', label: 'Kode Pos' },
            ].map((item) => (
              <TouchableOpacity
                key={item.id}
                style={[styles.tab, tab === item.id && styles.tabOn]}
                disabled={tabDisabled[item.id]}
                onPress={() => {
                  if (tabDisabled[item.id]) return;
                  setQuery('');
                  setTab(item.id);
                }}
              >
                <Text style={[
                  styles.tabText,
                  tab === item.id && styles.tabTextOn,
                  tabDisabled[item.id] && styles.tabTextOff,
                ]}
                >
                  {item.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          <TextInput
            style={styles.search}
            value={query}
            onChangeText={setQuery}
            placeholder={tab === 'kodepos' ? 'Cari kecamatan / kode pos' : 'Cari…'}
            placeholderTextColor="#94A3B8"
          />
          {loading ? (
            <ActivityIndicator style={{ marginVertical: 24 }} color="#64748B" />
          ) : (
            <ScrollView style={styles.list} keyboardShouldPersistTaps="handled">
              {filtered.length === 0 ? (
                <Text style={styles.empty}>Tidak ada hasil</Text>
              ) : filtered.map((row) => {
                const key = `${row.kode}-${row.kodepos || row.nama || ''}`;
                const label = tab === 'kodepos' ? (row.label || `${row.nama_kecamatan} - ${row.kodepos}`) : row.nama;
                const onPress = tab === 'provinsi'
                  ? () => chooseProvinsi(row)
                  : tab === 'kota'
                    ? () => chooseKota(row)
                    : () => choosePostal(row);
                return (
                  <TouchableOpacity key={key} style={styles.row} onPress={onPress}>
                    <Text style={styles.rowText}>{label}</Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(15,23,42,0.45)',
  },
  sheet: {
    width: '100%',
    maxWidth: 430,
    maxHeight: '80%',
    backgroundColor: '#fff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    padding: 16,
    paddingBottom: Platform.OS === 'ios' ? 28 : 16,
    zIndex: 2,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  title: { fontSize: 16, fontWeight: '700', color: '#0F172A' },
  tabs: { flexDirection: 'row', gap: 8, marginBottom: 10 },
  tab: {
    flex: 1,
    paddingVertical: 8,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    alignItems: 'center',
  },
  tabOn: { borderBottomColor: '#1E293B' },
  tabText: { fontSize: 12, fontWeight: '600', color: '#94A3B8' },
  tabTextOn: { color: '#0F172A' },
  tabTextOff: { color: '#CBD5E1' },
  search: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    color: '#0F172A',
    marginBottom: 8,
  },
  list: { maxHeight: 360 },
  row: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  rowText: { fontSize: 14, color: '#334155' },
  empty: { fontSize: 13, color: '#94A3B8', paddingVertical: 20, textAlign: 'center' },
});
